import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {parseNumberWithCommaDecimal} from "app/utils/stringUtil";
import {ChartUtil} from "app/utils/chartUtil";
import {useAppSelector} from "../../../store/hook";
import DashboardLoading from "./DashboardLoading";
import DashboardNoData from "./DashboardNoData";
import {DbUserService} from "../../../services/db/dbUserService";
import {ResponseResultCode} from "../../../model/response-api";
import {toast} from "react-toastify";
import DashboardCard from "./DashboardCard";
import {DashboardModuleProps} from "../../../model/db/dashboardModuleProps";

const defaultChartSeries: any = [
  {
    type: 'column',
    name: '부하별 사용량',
    data: [],
    color: {
      linearGradient: {x1: 0, y1: 0, x2: 0, y2: 1},
      stops: [
        [0, 'rgba(123,198,174,1)'],
        [1, 'rgba(103,164,210,1)'],
      ],
    },
  },
  {
    yAxis: 0,
    type: 'line',
    name: '전력 사용량',
    data: [],
    color: '#E2A215',
    marker: {
      lineColor: '#E2A215',
    },
  },
  {
    yAxis: 0,
    type: 'line',
    name: "작년 동시간 전력 사용량",
    data: [],
    color: '#A9A9A9',
    marker: {
      lineColor: '#A9A9A9',
    },
  },
];

const options = {
  chart: {
    type: 'column',
    height: 277,
    spacingTop: 0,
    spacingBottom: 0,
    marginTop: 80,
  },
  title: {text: ''},
  exporting: {enabled: false},
  credits: {enabled: false},
  legend: {
    align: 'right',
    verticalAlign: 'top',
    layout: 'horizontal',
    symbolHeight: 10,
    padding: 10,
    itemStyle: {
      fontSize: '0.75rem',
      color: '#444444',
    },
  },
  plotOptions: {
    column: {
      borderWidth: 0,
      pointWidth: 20,
      borderRadiusTopLeft: '10%',
      borderRadiusTopRight: '10%'
    },
    line: {
      dashStyle: 'line',
      lineWidth: 1.5,
      marker: {
        symbol: 'circle',
        lineWidth: 1.5,
        fillColor: '#fff'
      }
    },
  },
  tooltip: {
    formatter: function (this: any) {
      let tooltip = `<span><strong style="font-size: 0.75rem;">${this.x}</strong></span><br/>`;

      this.points.forEach((p: any, i: number) => {
        let s = 2;

        if (i === 0 || i === 1) {
          s = -1;
        }

        let r = Math.pow(10, s);

        tooltip += `<span style="color: ${p.series.color};">●&nbsp;</span>${p.series.name}: <strong style="font-size: 0.75rem;">${parseNumberWithCommaDecimal(Math.round(p.y * r) / r)}</strong> kWh<br/>`;
      });

      return tooltip;
    },
    style: {
      fontSize: '0.75rem',
    }
  },
  xAxis: {
    categories: [],
    title: undefined,
    min: 0,
    crosshair: true,
    lineColor: '#999',
    lineWidth: 1,
    labels: {
      style: {
        color: '#444',
        fontSize: '0.7rem',
      },
    },
  },
  yAxis: [
    {
      title: {
        text: '(kWh)', // 첫 번째 yAxis만 남기기
        align: 'high',
        rotation: 0,
        offset: 0,
        y: -25,
        style: {
          color: '#444',
          fontSize: '0.65rem',
        },
      },
      min: 0,
      softMax: 10,
      lineColor: '#999',
      lineWidth: 1,
      gridLineColor: '#CCC',
      gridLineWidth: 1,
      gridLineDashStyle: 'ShortDash',
      labels: {
        x: -5,
        y: 4,
        style: {
          color: '#444',
          fontSize: '0.7rem',
        },
        formatter: function (this: any) {
          return parseNumberWithCommaDecimal(this.value);
        },
      },
    },
  ],
  series: defaultChartSeries,
  responsive: {
    rules: [
      {
        condition: {
          maxWidth: 500,
        },
        chartOptions: {
          chart: {
            spacingTop: 20,
            marginTop: 20,
          },
          xAxis: {
            labels: {
              style: {
                fontSize: '0.5rem',
              },
            },
          },
          yAxis: [
            {
              title: {
                y: -10,
                style: {
                  fontSize: '0.5rem',
                },
              },
              labels: {
                style: {
                  fontSize: '0.5rem',
                },
              },
            },
            {
              title: {
                y: -10,
                style: {
                  fontSize: '0.5rem',
                },
              },
              labels: {
                style: {
                  fontSize: '0.5rem',
                },
              },
            },
          ],
          legend: {
            align: 'center',
            verticalAlign: 'bottom',
            layout: 'horizontal',
            itemStyle: {
              fontSize: '0.6rem',
            },
          },
          tooltip: {
            style: {
              fontSize: '0.6rem',
            }
          },
          plotOptions: {
            column: {
              pointWidth: undefined,
            },
            line: {
              lineWidth: 1,
              marker: {
                lineWidth: 1,
                width: 2,
                height: 2,
                radius: 2
              },
            },
          },
        },
      },
    ],
  },
};

export default function LoadUsage(props: Readonly<DashboardModuleProps>) {
  const {t} = useTranslation();
  const hasFacility = useAppSelector((state) => state.mgResource.hasFacility);
  const [chartOption, setChartOption] = useState<any>(options);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const chartCategories = ChartUtil.getHourRangeForCategory();

  useEffect(() => {
    DbUserService.getInstance().fetchLoadData().then(response => {
      if (response && response.resultCode === ResponseResultCode.Y && response.data) {
        const series = defaultChartSeries;
        series[0].data = response.data.facilPwrUse;
        series[1].data = response.data.pwrUse;
        series[2].data = response.data.pwrUseLastYear;

        setChartOption({
          ...options,
          xAxis: {
            categories: chartCategories,
          },
          series: series
        })
      } else {
        toast.error('부하별 사용량 데이터를 불러오지 못했습니다. 다시 시도해 주세요.');
      }
    }).catch(() => {
      toast.error('데이터를 불러오는 중 오류가 발생했습니다. 다시 시도해 주세요.');
    }).finally(() => {
      setIsLoading(false);
    });
  }, []);

  return (
    <DashboardCard
      title="부하별 사용량"
      isConfigMode={props.isConfigMode ?? false}
      moreButtonVisible={true}
      moreButtonUrl="/pc/eu/usageload"
      dashboardConfig={props.dashboardConfig}
    >
      <DashboardLoading isLoading={isLoading} />
      {/*{!isLoading && !hasFacility ? (*/}
      {/*    <DashboardNoData message="등록된 부하 설비가 없습니다." />*/}
      {/*) : (*/}
        <div className="inner-flex1">
          <div className="chart">
            {options && (
              <HighchartsReact highcharts={Highcharts} options={chartOption}/>
            )}
          </div>
        </div>
      {/*)}*/}
    </DashboardCard>
  )
}